import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Dialog,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfResourceLibraryDetail } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import ResourceLibraryItemFilter from "./ResourceLibraryItemFilter";
import ResourceLibraryItemsList from "./ResourceLibraryItemsList";
import ResourceLibraryUpdateForm from "./ResourceLibraryUpdateForm";

const useStyles = makeStyles((theme: Theme) => ({
  tableTitle: {
    margin: "20px 0",
  },
}));

const ResourceLibraryDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const navigate = useNavigate();
  const styles = useStyles(useTheme());
  const { id } = useParams<{ id: string }>();
  const { resourceLibraryService, resourceLibrariesUrl } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const resourceLibraryResult = useQuery<ResponseOfResourceLibraryDetail>(
    ["resourceLibrary", id],
    () => resourceLibraryService.getResourceLibraryById(id!),
    {
      enabled: !!id,
    }
  );

  const resourceLibrary = resourceLibraryResult.data?.data;
  useTitle(resourceLibrary?.name || "Resource Library Details");

  if (!id) return <Navigate to={resourceLibrariesUrl()} replace />;
  if (resourceLibraryResult.isLoading) return <Loader />;
  if (resourceLibraryResult.isError) {
    console.log(resourceLibraryResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="resourceLibraryLoadError" />
        </Alert>
      </Box>
    );
  }

  const onResourceLibraryUpdated = () => {
    queryClient.invalidateQueries(["resourceLibrary"]);

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "resourceLibraryUpdated" }))
    );

    updateOpenActions.setFalse();
  };

  const handleDelete = async () => {
    await registerPromise(resourceLibraryService.deleteResourceLibrary(id));
    queryClient.invalidateQueries("resourceLibraries");
    navigate(resourceLibrariesUrl());
  };

  const rows = [
    {
      title: t({ id: "resourceLibraryListNameLabel" }),
      component: resourceLibrary?.name && <span>{resourceLibrary.name}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: <span>{resourceLibrary?.organizationName}</span>,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: resourceLibrary?.description && (
        <span>{resourceLibrary.description}</span>
      ),
    },
    {
      title: t({ id: "resourceLibraryFormContentNameLabel" }),
      component: resourceLibrary?.contentName && (
        <span>{resourceLibrary.contentName}</span>
      ),
    },
    {
      title: t({ id: "resourceLibraryFormPathLabel" }),
      component: resourceLibrary?.libraryPath && (
        <span>{resourceLibrary.libraryPath}</span>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={resourceLibrariesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>{resourceLibrary?.name}</h1>
      <Box mb={3} display="flex" justifyContent="center">
        <Fab
          color="primary"
          onClick={updateOpenActions.setTrue}
          data-cy="editButton"
        >
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>
      <Typography variant="h5" sx={styles.tableTitle}>
        Resource Library
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row">
                  <b>{title}</b>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box my={2} />
      <ResourceLibraryItemFilter />
      <ResourceLibraryItemsList resourceLibrary={resourceLibrary!} />

      <Dialog open={updateOpen} disableEnforceFocus>
        <ResourceLibraryUpdateForm
          resourceLibrary={resourceLibrary!}
          onResourceLibraryUpdated={onResourceLibraryUpdated}
          onCancel={updateOpenActions.setFalse}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="resourceLibraryDeleteTitle"
        contentKey="resourceLibraryDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default ResourceLibraryDetails;
